import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Bell, Mail, MessageSquare } from "lucide-react";
import { Button, Card, CardContent } from "@eduviaos/ui-kit";
import { useAuth } from "@/auth/AuthProvider";
import { FirstRunShell } from "@/components/FirstRunShell";
import { buildFirstRunSteps, firstRunEyebrow } from "@/components/firstRunSteps";
import { useEnabledModules } from "@/hooks/useEnabledModules";

const DEFAULT_COMMS = [
  {
    icon: Mail,
    title: "Welcome email",
    body: "Sent when a learner accepts their portal invite — confirms login details and your centre name.",
  },
  {
    icon: MessageSquare,
    title: "Fee reminder (WhatsApp)",
    body: "Nudges learners or parents when a fee plan installment is due in the next 3 days.",
  },
  {
    icon: Bell,
    title: "Attendance alert",
    body: "Notifies admins when a learner hasn't checked in by noon on a scheduled study day.",
  },
] as const;

/**
 * Phase SC — first-run step 5 (study centre only): review default comms.
 *
 * Full template authoring lives under Settings → Notification triggers after
 * go-live. This step sets expectations so the principal knows what will fire
 * automatically once the centre is live.
 */
export function FirstRunCommsPage() {
  const navigate = useNavigate();
  const { firstRunComplete, status } = useAuth();
  const { vertical } = useEnabledModules();
  const resolvedVertical = vertical ?? "school";

  if (resolvedVertical !== "study_centre") {
    return <Navigate to="/dashboard" replace />;
  }

  if (status === "authed" && firstRunComplete === true) {
    return <Navigate to="/dashboard" replace />;
  }

  const steps = buildFirstRunSteps("study_centre");

  return (
    <FirstRunShell
      steps={steps}
      activeStep="comms"
      eyebrow={firstRunEyebrow("study_centre", "comms")}
    >
      <header className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 grid gap-3 motion-safe:duration-500">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          Review your communication defaults
        </h1>
        <p className="text-muted-foreground max-w-xl text-sm leading-relaxed sm:text-base">
          EduViaOS ships with sensible notification triggers for study centres. You can customise
          copy and channels later from Settings → Notification triggers.
        </p>
      </header>

      <div className="mt-8 grid gap-4 sm:grid-cols-3">
        {DEFAULT_COMMS.map(({ icon: Icon, title, body }) => (
          <Card key={title} className="motion-safe:animate-in motion-safe:fade-in">
            <CardContent className="grid gap-3 p-5">
              <div className="bg-primary/10 text-primary inline-flex size-9 items-center justify-center rounded-lg">
                <Icon aria-hidden="true" className="size-4" />
              </div>
              <h2 className="font-medium">{title}</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">{body}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center gap-3">
        <Button onClick={() => navigate("/first-run/go-live")}>
          Continue
          <ArrowRight aria-hidden="true" className="size-4" />
        </Button>
        <Button variant="ghost" onClick={() => navigate("/first-run/go-live")}>
          Skip for now
        </Button>
      </div>
    </FirstRunShell>
  );
}
